import React from 'react';
import {
  FileText,
  Download,
  AlertCircle,
  Shield,
  CheckCircle,
  FileCheck
} from 'lucide-react';

const ResourcesPage: React.FC = () => {
  const RENTAL_AGREEMENT_URL =
    'https://firebasestorage.googleapis.com/v0/b/rentsmart-6e31b.appspot.com/o/doc-assets%2FRESIDENTIAL%20TENANCY%20AGREEMENT.docx?alt=media&token=a2204c1e-b0f1-450e-a680-04ed94e291db';
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <FileText className="w-16 h-16 mr-4" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Rental Resources
          </h1>
          <p className="text-xl text-center text-blue-100 max-w-3xl mx-auto">
            Professional templates and documents to help landlords and tenants
            manage their rental agreements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Free Rental Agreement Templates
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            We provide professionally crafted rental agreement templates to help
            streamline the rental process. These templates are designed to
            comply with Irish rental law and protect both landlords and tenants.
          </p>
        </div>

        {/* Template Card */}
        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileCheck className="w-12 h-12 text-white mr-4" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      Standard Residential Tenancy Agreement
                    </h3>
                    <p className="text-blue-100 mt-1">
                      Comprehensive agreement for Irish residential rentals
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                  What's Included:
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Tenant and landlord information sections</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Property details and rent payment terms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Security deposit and maintenance clauses</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Tenant rights and landlord obligations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Termination and notice period provisions</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-1" />
                  <div className="text-sm text-gray-600">
                    <p className="font-semibold mb-1">Format: PDF Document</p>
                    <p>Easy to fill, print, and sign</p>
                  </div>
                </div>
              </div>

              <button
                className="block w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-4 rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 text-center font-semibold text-lg cursor-pointer"
                onClick={() => {
                  window.open(RENTAL_AGREEMENT_URL, '_blank');
                }}
              >
                <div className="flex items-center justify-center">
                  <Download className="w-6 h-6 mr-2" />
                  Download Template
                </div>
              </button>
            </div>
          </div>

          {/* Info Card */}
          <div className="space-y-6">
            {/* Why Use Our Templates */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-800">
                  Why Use Our Templates?
                </h3>
              </div>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-1" />
                  <span>
                    <strong>Legally Sound:</strong> Templates designed with
                    Irish rental law in mind
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-1" />
                  <span>
                    <strong>Save Time:</strong> Pre-formatted and ready to use
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-1" />
                  <span>
                    <strong>Professional:</strong> Clear, comprehensive, and
                    easy to understand
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-1" />
                  <span>
                    <strong>Free:</strong> No cost to download and use
                  </span>
                </li>
              </ul>
            </div>

            {/* Quick Tips */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Quick Tips
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2 font-bold">1.</span>
                  <span>Read the entire agreement before signing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2 font-bold">2.</span>
                  <span>Fill in all required fields accurately</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2 font-bold">3.</span>
                  <span>Both parties should keep signed copies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2 font-bold">4.</span>
                  <span>Consider having witnesses present</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 shadow-md">
          <div className="flex items-start">
            <AlertCircle className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                Important Legal Disclaimer
              </h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>
                  <strong>Please read carefully:</strong> These rental agreement
                  templates are provided as a convenience and for informational
                  purposes only. They are not a substitute for professional
                  legal advice.
                </p>
                <p>
                  <strong>No Legal Advice:</strong> RentSmart.ie is not a law
                  firm and does not provide legal advice. The templates provided
                  are generic in nature and may not address specific
                  circumstances or comply with all local regulations.
                </p>
                <p>
                  <strong>Professional Review Recommended:</strong> We strongly
                  recommend that both landlords and tenants have any rental
                  agreement reviewed by a qualified solicitor before signing.
                  Irish rental law is complex and subject to change.
                </p>
                <p>
                  <strong>No Warranty:</strong> While we strive to provide
                  accurate and up-to-date templates, RentSmart.ie makes no
                  representations or warranties regarding the legal validity,
                  completeness, or suitability of these templates for your
                  specific situation.
                </p>
                <p>
                  <strong>Limitation of Liability:</strong> RentSmart.ie and its
                  affiliates shall not be liable for any damages or losses
                  arising from the use of these templates. Use of these
                  templates is at your own risk.
                </p>
                <p>
                  <strong>Irish Law Applies:</strong> These templates are
                  designed with Irish rental law in mind. If you are renting
                  property outside of Ireland, different laws may apply.
                </p>
                <p className="pt-2 border-t border-yellow-200 mt-4">
                  For official guidance, please consult the{' '}
                  <a
                    href="https://www.rtb.ie/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    Residential Tenancies Board (RTB)
                  </a>{' '}
                  or seek advice from a qualified legal professional.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">
            Additional Resources
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="https://www.rtb.ie/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-300"
            >
              <h4 className="font-semibold text-gray-800 mb-2">
                Residential Tenancies Board
              </h4>
              <p className="text-sm text-gray-600">
                Official Irish rental authority
              </p>
            </a>
            <a
              href="https://www.citizensinformation.ie/en/housing/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-300"
            >
              <h4 className="font-semibold text-gray-800 mb-2">
                Citizens Information
              </h4>
              <p className="text-sm text-gray-600">
                Housing rights and information
              </p>
            </a>
            <a
              href="https://www.threshold.ie/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-300"
            >
              <h4 className="font-semibold text-gray-800 mb-2">Threshold</h4>
              <p className="text-sm text-gray-600">
                National housing charity for advice
              </p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
